import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { X, Folder } from 'lucide-react';

import useProducts from '../hooks/useProducts';
import useCategories from '../hooks/useCategories';
import ProductCard from '../components/product/ProductCard';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'title', label: 'Name (A–Z)' },
];

const LIMIT = 12;

export default function ProductList() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'newest';
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

  const { categories } = useCategories();
  const { items, pages, total, loading, error } = useProducts({ category, q, page, limit: LIMIT, sort });

  // Local search box mirrors ?q= (submits to update the URL).
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);

  // Update URL params; changing any filter resets pagination (unless page itself changes).
  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, v);
    });
    if (!('page' in patch)) next.delete('page');
    setParams(next);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    update({ q: search.trim() });
  };

  const onClearSearch = () => {
    setSearch('');
    update({ q: '' });
  };

  const activeCategory = categories.find((c) => c.slug === category);

  const searchField = (
    <div className="ss-admin-search__field" style={{ maxWidth: 200 }}>
      <input
        type="search"
        className="ss-input"
        placeholder="Search…"
        aria-label="Search products"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {search && (
        <button
          type="button"
          className="ss-admin-search__clear"
          onClick={onClearSearch}
          aria-label="Clear search"
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const sortSelect = (
    <select className="ss-select" value={sort} onChange={(e) => update({ sort: e.target.value })}>
      {SORTS.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>
  );

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <div className="ss-shop-heading">
        <h1>{activeCategory ? activeCategory.name : 'Shop all products'}</h1>
        <div className="ss-only-desktop" style={{ display: 'flex', gap: 'var(--ss-space-3)', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 'var(--ss-space-2)', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Sort</span>
            {sortSelect}
          </label>
          <form
            role="search"
            onSubmit={onSearchSubmit}
            style={{ display: 'flex', gap: 'var(--ss-space-2)' }}
          >
            {searchField}
            <Button type="submit" variant="ghost" size="sm">Search</Button>
          </form>
        </div>
      </div>
      <p className="ss-shop-contact">
        If you don't see what you want <Link to="/contact">Contact Us</Link>
      </p>
      <form role="search" onSubmit={onSearchSubmit} className="ss-shop-search-mobile ss-only-mobile">
        {searchField}
        <Button type="submit" variant="ghost" size="sm">Search</Button>
      </form>
      <label className="ss-shop-sort-mobile ss-only-mobile" style={{ gap: 'var(--ss-space-2)', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Sort</span>
        {sortSelect}
      </label>

      <div className="ss-shop-toolbar">
        <div className="ss-chips" role="group" aria-label="Filter by category">
          <button
            type="button"
            className={`ss-cat-chip ${!category ? 'is-active' : ''}`}
            onClick={() => update({ category: '' })}
          >
            All
          </button>
          {/* useCategories() returns every category at every depth; restrict the
              chip row to roots so a subcategory doesn't sit flat next to them —
              deeper drill-down belongs to /category/*. A root with its own
              subcategories can never hold products directly (leaf-only
              enforcement), so filtering by it here would always come back
              empty — send it into the category tree instead, marked with a
              folder icon so it reads as "browse" rather than "filter". */}
          {categories
            .filter((c) => !c.parentId)
            .map((c) =>
              c.isLeaf ? (
                <button
                  key={c._id || c.id}
                  type="button"
                  className={`ss-cat-chip ${category === c.slug ? 'is-active' : ''}`}
                  onClick={() => update({ category: c.slug })}
                >
                  {c.name}
                </button>
              ) : (
                <Link key={c._id || c.id} to={`/category/${c.slug}`} className="ss-cat-chip ss-cat-chip--folder" title="Browse subcategories">
                  <Folder size={14} aria-hidden="true" />
                  {c.name}
                </Link>
              )
            )}
        </div>
      </div>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading products" size={32} /></div>
      ) : error ? (
        <div className="ss-state">
          <h2>Couldn’t load products</h2>
          <p>Please try again in a moment.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="ss-state">
          {q ? (
            <>
              <h2>No matches</h2>
              <p>Nothing matched “{q}”. Try a different search.</p>
            </>
          ) : category ? (
            <>
              <h2>Coming soon</h2>
              <p>We’re still growing this category. Check back soon!</p>
            </>
          ) : (
            <>
              <h2>No products yet</h2>
              <p>Our shelves are being stocked — check back soon.</p>
            </>
          )}
        </div>
      ) : (
        <>
          <p style={{ color: 'var(--ss-brown-hover)', marginBottom: 'var(--ss-space-4)' }}>
            {total} product{total === 1 ? '' : 's'}
            {pages > 1 && `, page ${page} of ${pages}`}
          </p>
          <div className="ss-product-grid">
            {items.map((p) => (
              <ProductCard key={p._id || p.id} product={p} />
            ))}
          </div>

          {pages > 1 && (
            <nav className="ss-pagination" aria-label="Pagination">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <span aria-current="page">Page {page} of {pages}</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= pages}
                onClick={() => update({ page: String(page + 1) })}
              >
                Next
              </Button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}
